import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import type { BadStatusError } from '@confluence/network';
import { useExperimentalLazyQuery } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalLazyQuery.ts';
import { MediaType } from '@confluence/graphql/entry-points/MediaType.ts';

import type {
	RelationsForContentQuery as RelationsForContentQueryData,
	RelationsForContentQueryVariables,
} from '../__types__/RelationsForContentQuery.ts';

type RelationsForContentResponse = {
	results: Array<{
		name?: string;
		relationData?: {
			friendlyCreatedDate?: string | null;
		};
		source?: {
			accountId?: string | null;
			displayName?: string | null;
			profilePicture?: {
				path?: string;
			};
		};
		_expandable?: Record<string, unknown>;
		_links?: Record<string, unknown>;
	}>;
	start: number;
	limit: number;
	size: number;
	_links: Record<string, unknown>;
};

export const useExperimentalRelationsForContentQuery = ({
	variables,
	onCompleted,
	onError,
}: {
	variables: RelationsForContentQueryVariables;
	onCompleted?: (data: RelationsForContentQueryData) => void;
	onError?: (error: ApolloErrorInterop) => void;
}) => {
	const { relationName, sourceType, targetKey, targetStatus } = variables;

	if (!relationName) {
		throw new Error('relationName is missing');
	}
	if (!sourceType) {
		throw new Error('sourceType is missing');
	}
	if (!targetKey) {
		throw new Error('targetKey is missing');
	}
	if (!targetStatus) {
		throw new Error('targetStatus is missing');
	}

	const parseResult = (response: RelationsForContentResponse): RelationsForContentQueryData => {
		const mapSource = (source: RelationsForContentResponse['results'][number]['source']) => {
			if (!source) return null;

			return {
				displayName: source.displayName ?? null,
				accountId: source.accountId ?? null,
				profilePicture: source.profilePicture?.path ? { path: source.profilePicture.path } : null,
			};
		};

		const mapRelationData = (
			relationData: RelationsForContentResponse['results'][number]['relationData'],
		) => {
			if (!relationData) return null;

			return {
				friendlyCreatedDate: relationData.friendlyCreatedDate ?? null,
			};
		};

		return {
			experimentalRelationsForContent: {
				results: response.results.map((result) => ({
					source: mapSource(result.source),
					relationData: mapRelationData(result.relationData),
				})),
			},
		};
	};

	return useExperimentalLazyQuery<
		RelationsForContentQueryData,
		RelationsForContentQueryVariables,
		RelationsForContentResponse
	>({
		url: `/wiki/rest/api/relation/${relationName}/to/content/${targetKey}/from/${sourceType}/?targetStatus=${targetStatus}&expand=source,relationData`,
		method: 'GET',
		headers: {
			'Content-Type': MediaType.JSON,
		},
		parseResult,
		variables,
		onCompleted,
		onError,
		transformError: (error: Error | BadStatusError) => {
			// Check if it's a BadStatusError with response status
			if ('response' in error && error.response?.status) {
				const status = error.response.status;
				if (status === 403) {
					return new Error('User does not have permission to view the relationship');
				} else if (status === 404) {
					return new Error('Target entity does not exist');
				}
			}
			return new Error('Unknown error');
		},
	});
};
